class CompareUtils {
  static cookieName = 'hypertheme:compare-products';

  /**
   *
   * @returns {array}
   */
  static getSelectedProducts() {
    const products = FoxTheme.utils.getStorage(this.cookieName);
    return products || [];
  }

  static setSelectedProducts(products) {
    if (products.length > 0) {
      FoxTheme.utils.setStorage(this.cookieName, products);
    } else {
      localStorage.removeItem(this.cookieName);
    }
  }

  static getProductsCount() {
    return this.getSelectedProducts().length;
  }

  /**
   * Check the given product id in compare list.
   * @param {*} productId
   * @returns {boolean}
   */
  static isProductInList(productId) {
    const products = this.getSelectedProducts();

    return products.find((product) => product.id === productId);
  }

  /**
   *
   * @param {*} productId
   * @param {*} productUrl
   * @returns {boolean} True if the product added to list.
   */
  static addToList(productId, productUrl) {
    let products = FoxTheme.utils.getStorage(this.cookieName) || [];

    if (products.length < FoxTheme.compare.maxProductsInCompare) {
      if (!products.some((product) => product.id === productId)) {
        products.push({ id: productId, url: productUrl });
      }

      this.setSelectedProducts(products);
      this.updateCompareCheckboxes(products);

      FoxTheme.pubsub.publish('compare:add', { products: products, productId: productId, productUrl: productUrl });

      return true;
    } else {
      alert(FoxTheme.compare.alertMessage);

      return false;
    }
  }

  static removeFromList(productId) {
    let products = this.getSelectedProducts();
    if (products) {
      products = products.filter((product) => product.id !== productId);
    }
    this.setSelectedProducts(products);
    this.updateCompareCheckboxes(products);
    FoxTheme.pubsub.publish('compare:remove', { products: products, productId: productId });
  }

  static clearList() {
    const products = this.getSelectedProducts();
    if (products) {
      products.forEach((product) => {
        this.removeFromList(product.id);
      });
    }
  }

  static updateCompareCheckboxes(products = null) {
    products = products || this.getSelectedProducts();
    document.querySelectorAll('.js-compare-checkbox').forEach((checkbox) => {
      checkbox.checked = products.filter((product) => product.id === checkbox.dataset.productId).length;
    });
  }
}

class CompareSwitch extends HTMLElement {
  constructor() {
    super();

    this.cookieName = 'hypertheme:compare-is-active';

    this.classes = {
      isActive: 'is-product-comparing',
    };

    this.checkboxEl = this.querySelector('input');
    this.onChangeHandler = this.onChange.bind(this);
  }

  connectedCallback() {
    this.init();
    this.checkboxEl.addEventListener('change', this.onChangeHandler);
  }

  disconnectedCallback() {
    this.checkboxEl.removeEventListener('change', this.onChangeHandler);
  }

  init() {
    if (FoxTheme.config.hasLocalStorage) {
      const isActive = window.localStorage.getItem(this.cookieName) === 'true';
      if (isActive) {
        this.checkboxEl.checked = isActive;
        this.handleChange(isActive);
      }
    }
  }

  onChange(evt) {
    evt.preventDefault();

    this.handleChange(this.checkboxEl.checked);
  }

  handleChange(isActive) {
    requestAnimationFrame(() => {
      if (isActive) {
        document.body.classList.add(this.classes.isActive);
      } else {
        document.body.classList.remove(this.classes.isActive);
      }
    });

    FoxTheme.pubsub.publish('compare:toggle', { isActive: isActive });

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, isActive);
    }
  }
}
customElements.define('compare-switch', CompareSwitch);

class CompareCheckbox extends HTMLElement {
  constructor() {
    super();

    this.checkboxEl = this.querySelector('input');
    this.onChangeHandler = this.onChange.bind(this);
  }

  connectedCallback() {
    this.init();
    this.checkboxEl.addEventListener('change', this.onChangeHandler);
  }

  disconnectedCallback() {
    this.checkboxEl.removeEventListener('change', this.onChangeHandler);
  }

  init() {
    const { productId } = this.checkboxEl.dataset;
    this.checkboxEl.checked = CompareUtils.isProductInList(productId);
  }

  onChange(evt) {
    evt.preventDefault();

    const { productId, productUrl } = this.checkboxEl.dataset;

    if (this.checkboxEl.checked) {
      const isAdded = CompareUtils.addToList(productId, productUrl);
      if (!isAdded) {
        this.checkboxEl.checked = false;
      }
    } else {
      CompareUtils.removeFromList(productId);
    }
  }
}
customElements.define('compare-checkbox', CompareCheckbox);

class CompareRemove extends HTMLButtonElement {
  constructor() {
    super();

    this.onClickHandler = this.onClick.bind(this);
    this.addEventListener('click', this.onClick.bind(this));
  }

  onClick(event) {
    event.preventDefault();

    const { productId } = this.dataset;
    const { target } = event;

    CompareUtils.removeFromList(productId);

    const compareModal = target.closest('.compare-drawer');
    if (compareModal) {
      compareModal.updateProductsCount();
    }

    if (CompareUtils.getProductsCount() < 2) {
      compareModal && compareModal.hide();
    } else {
      const compareTable = target.closest('.compare-table');
      if (compareTable) {
        const cell = target.closest('td');
        const colIndex = cell.cellIndex;

        const rows = compareTable.querySelectorAll('tr');
        rows.forEach((row) => {
          const cellToRemove = row.cells[colIndex];
          if (cellToRemove) {
            cellToRemove.remove();
          }
        });
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }
}
customElements.define('compare-remove', CompareRemove, { extends: 'button' });

class CompareBar extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      openBtn: '.compare-bar__open',
      clearBtns: ['.compare-bar__clear'],
      toggleList: '.compare-bar__toggle-list',
      list: '.compare-bar__list',
      counter: '.compare-bar__counter',
    };

    this.classes = {
      listVisible: 'is-show-list',
    };

    this.abortController = new AbortController();
    this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
    this.atLeastItemToShow = Math.min(5, FoxTheme.compare.maxProductsInCompare); // Total compare products and placeholders.

    this.elements.clearBtns.forEach((clearBtn) => {
      clearBtn.addEventListener('click', this.handleClear.bind(this), {
        signal: this.abortController.signal,
      });
    });

    this.elements.toggleList.addEventListener('click', this.handleToggleList.bind(this), {
      signal: this.abortController.signal,
    });

    this.itemAddUnsubscriber = FoxTheme.pubsub.subscribe('compare:add', this.onItemAdded.bind(this));
    this.itemRemoveUnsubscriber = FoxTheme.pubsub.subscribe('compare:remove', this.onItemRemoved.bind(this));
  }

  itemAddUnsubscriber = undefined;
  itemRemoveUnsubscriber = undefined;

  connectedCallback() {
    this.renderBar();
  }

  disconnectedCallback() {
    this.abortController.abort();

    if (this.itemAddUnsubscriber) {
      this.itemAddUnsubscriber();
    }

    if (this.itemRemoveUnsubscriber) {
      this.itemRemoveUnsubscriber();
    }
  }

  onItemAdded(event) {
    const { products, productId, productUrl } = event;

    this.appendBarItem(products, productId, productUrl);
    this.toggleCompareButton(products.length);
    this.updateCounters(products.length);
  }

  onItemRemoved(event) {
    const { products, productId } = event;
    const itemToDelete = this.elements.list.querySelector(`.compare-bar__item[data-product-id="${productId}"]`);

    itemToDelete && itemToDelete.remove();
    this.toggleCompareButton(products.length);
    this.updateCounters(products.length);
    this.removePlaceholdes();
    this.addPlaceholders(products.length);
  }

  async renderBar() {
    const products = CompareUtils.getSelectedProducts();

    let output = '';
    const compareItemHtmls = await this.getBarItems(products);
    compareItemHtmls.forEach((htmlStr) => {
      output += htmlStr;
    });

    this.removePlaceholdes();
    this.elements.list.innerHTML = output;
    this.addPlaceholders(products.length);

    this.toggleCompareButton(products.length);
    this.updateCounters(products.length);
  }

  async getBarItems(products) {
    const tmps = [];
    await Promise.all(
      products.map(async (product) => {
        await fetch(`${product.url}?sections=product-compare-bar-item`)
          .then((response) => response.json())
          .then((response) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(response['product-compare-bar-item'], 'text/html');
            const itemHtml = doc.querySelector('.shopify-section');

            tmps[product.id] = itemHtml.innerHTML;
          });
      })
    );

    const returns = [];
    products.forEach((product) => returns.push(tmps[product.id]));
    return returns;
  }

  async appendBarItem(products, productId, productUrl) {
    this.removePlaceholdes();
    this.elements.list.insertAdjacentHTML(
      'beforeend',
      `<li class="compare-bar__item" data-product-id="${productId}"></li>`
    );
    this.addPlaceholders(products.length);

    await fetch(`${productUrl}?sections=product-compare-bar-item`)
      .then((response) => response.json())
      .then((response) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(response['product-compare-bar-item'], 'text/html');
        const itemHtml = doc.querySelector('.shopify-section');

        const itemEl = this.elements.list.querySelector('[data-product-id="' + productId + '"]');
        if (itemEl) {
          itemEl.outerHTML = itemHtml.innerHTML;
        }
      });
  }

  handleToggleList(event) {
    event.preventDefault();

    if (this.classList.contains(this.classes.listVisible)) {
      this.classList.remove(this.classes.listVisible);
    } else {
      this.classList.add(this.classes.listVisible);
    }
  }

  handleClear() {
    CompareUtils.clearList();
  }

  toggleCompareButton(count) {
    this.elements.openBtn.disabled = !(count > 1);
  }

  updateCounters(count) {
    this.elements.counter.innerText = count;
  }

  removePlaceholdes() {
    Array.from(this.elements.list.querySelectorAll('li')).forEach((li) => {
      if (li.classList.contains('compare-bar__item-placeholder')) {
        li.remove();
      }
    });
  }

  addPlaceholders(count) {
    const placeholderCount = this.atLeastItemToShow - count;

    for (let i = 0; i < placeholderCount; i++) {
      const placeholderItem = document.createElement('li');

      placeholderItem.classList.add('compare-bar__item', 'compare-bar__item-placeholder');
      this.elements.list.appendChild(placeholderItem);
    }
  }
}
customElements.define('compare-bar', CompareBar);

class CompareDrawer extends DrawerComponent {
  constructor() {
    super();

    this.selectors = {
      table: '.compare-table',
    };

    this.classesToRemoveOnLoad = 'drawer--loading';
  }

  get requiresBodyAppended() {
    return false;
  }

  get selector() {
    return '.product-comparison-table';
  }

  get sourceSelector() {
    return '#MainProduct-compare__rows';
  }

  get placeholderSelector() {
    return '#MainProduct-compare__placeholder';
  }

  getSectionId() {
    let sectionId = FoxTheme.ProductCompareSectionId || false;

    if (!sectionId) {
      // Get section id from overlay groups.
      const sectionEl = document.querySelector('.section-group-overlay-product-compare');
      if (sectionEl) {
        sectionId = FoxTheme.utils.getSectionId(sectionEl);
      }

      // Cache for better performance.
      FoxTheme.ProductCompareSectionId = sectionId;
    }

    return sectionId;
  }

  shouleBeShow() {
    const sectionId = this.getSectionId();
    return typeof sectionId === 'string';
  }

  prepareToShow() {
    super.prepareToShow();
    this.renderCompareTable();
  }

  async renderCompareTable() {
    const drawerContent = this.querySelector(this.selector);
    const sectionId = this.getSectionId();

    const products = CompareUtils.getSelectedProducts();

    this.updateProductsCount();

    this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
    const tableRows = this.elements.table.querySelectorAll('tbody tr');
    let compareRows = await this.getCompareTableRows(products);

    if (Shopify.designMode) {
      // Append placeholder items in editor mode.
      let maxPlaceholderItems = 3;

      if (compareRows.length == 2) {
        maxPlaceholderItems = 2;
      }

      let numberPlaceholderItems =
        compareRows.length > 0 ? maxPlaceholderItems - compareRows.length : maxPlaceholderItems;

      if (numberPlaceholderItems > 0) {
        const placeholderRows = this.getCompareTablePlaceholderRows(numberPlaceholderItems);

        compareRows = compareRows.concat(placeholderRows);

        this.updateProductsCount(maxPlaceholderItems);
      }
    }

    compareRows.forEach((compareRow) => {
      tableRows.forEach((row) => {
        const compareKey = row.dataset.compareKey;
        const compareCol = compareRow.querySelector(`td[data-compare-key="${compareKey}"]`);
        row.appendChild(compareCol);
      });
    });

    setTimeout(() => {
      this.classList.remove(this.classesToRemoveOnLoad);
    }, 300);
  }

  updateProductsCount(count) {
    this.style.setProperty('--number-items', count || CompareUtils.getProductsCount());
  }

  async getCompareTableRows(products) {
    const tmps = [];
    const sectionId = this.getSectionId();
    await Promise.all(
      products.map(async (product) => {
        await fetch(`${product.url}?section_id=${sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const rowTemplate = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector(this.sourceSelector);

            tmps[product.id] = rowTemplate.content.cloneNode(true);
          });
      })
    );

    const returns = [];
    products.forEach((product) => returns.push(tmps[product.id]));
    return returns;
  }

  getCompareTablePlaceholderRows(count) {
    const rowTemplate = this.querySelector(this.placeholderSelector);

    const returns = [];
    for (let i = 0; i < count; i++) {
      returns.push(rowTemplate.content.cloneNode(true));
    }

    return returns;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.handleAfterHide();
  }

  handleAfterHide() {
    super.handleAfterHide();
    // Clean the table.
    this.querySelectorAll('.compare-table > tbody > tr').forEach((row) => {
      const nonLabelCells = row.querySelectorAll('td:not(.td-label)');
      nonLabelCells.forEach((cell) => {
        cell.remove();
      });
    });

    this.classList.add(this.classesToRemoveOnLoad);
  }
}
customElements.define('compare-drawer', CompareDrawer);

/* Simme — simple two-product comparison. Normal page, no modal or nested scroll. */
(() => {
  'use strict';
  if (window.simmeSimpleCompareReady) return;
  window.simmeSimpleCompareReady = true;

  function init() {
    const MAX_PRODUCTS = 2;
    const root = window.Shopify?.routes?.root || '/';
    const page = document.getElementById('SimmeComparePage');
    const currency = window.Shopify?.currency?.active || 'DKK';
    let renderedProducts = [];
    let requestVersion = 0;
    let trayMessage = '';

    // Keep the simple pair separate from legacy comparison selections.
    CompareUtils.cookieName = 'simme:compare:simple:v2:' + (window.Shopify?.theme?.id || 'store');
    FoxTheme.compare.maxProductsInCompare = MAX_PRODUCTS;
    CompareUtils.updateCompareCheckboxes();

    const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[char]));
    const selections = () => CompareUtils.getSelectedProducts().slice(0, MAX_PRODUCTS);
    const handleOf = item => item.handle || new URL(item.url, location.origin).pathname.split('/').pop();
    const chosenVariant = product => product.variants.find(variant => String(variant.id) === String(product.chosen))
      || product.variants.find(variant => variant.available) || product.variants[0];
    const money = cents => new Intl.NumberFormat('da-DK', {
      minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2
    }).format(cents / 100) + (currency === 'DKK' ? ' kr.' : ' ' + currency);
    const availableText = variant => !variant?.available ? 'Ikki tøk á netinum'
      : variant.tracked && variant.quantity <= 0 && variant.policy === 'continue'
        ? 'Kann bíleggjast — ikki á goymslu' : 'Tøk at bíleggja á netinum';

    function productURL(value) {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !/\/products\/[^/]+$/.test(url.pathname)) throw new Error('Ógild vøra.');
      url.search = '';
      url.hash = '';
      return url;
    }

    function comparisonURL(items = selections()) {
      const url = new URL(root + 'search', location.origin);
      url.searchParams.set('view', 'samanber');
      if (items.length) url.searchParams.set('products', items.map(item => handleOf(item) + (item.variant ? ':' + item.variant : '')).join(','));
      if (window.Shopify?.theme?.role !== 'main' && window.Shopify?.theme?.id) {
        url.searchParams.set('preview_theme_id', window.Shopify.theme.id);
        url.searchParams.set('pb', '0');
      }
      return url;
    }

    const style = document.createElement('style');
    style.textContent = '.compare-bar,.compare-drawer{display:none!important}.ssp-tray{position:fixed;left:0;right:0;bottom:0;z-index:40;background:#fff;color:#202020;border-top:1px solid #ddd;box-shadow:0 -2px 12px #0001;padding:12px max(16px,calc((100vw - 1000px)/2));padding-bottom:max(12px,env(safe-area-inset-bottom));display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap}.ssp-tray[hidden]{display:none}.ssp-tray span{font-size:17px}.ssp-tray a{display:inline-flex;align-items:center;justify-content:center;min-height:48px;padding:10px 22px;border-radius:5px;background:#222;color:#fff;text-decoration:none;font-size:18px}.ssp-tray button,.ssp-pdp{min-height:44px;border:0;background:transparent;color:#333;font:inherit;text-decoration:underline;text-underline-offset:4px;cursor:pointer}.ssp-tray .ssp-tray-message{flex-basis:100%;font-size:16px;margin:0}.ssp-tray :focus-visible,.ssp-pdp:focus-visible{outline:3px solid #967000;outline-offset:4px}';
    document.head.append(style);
    const tray = document.createElement('section');
    tray.className = 'ssp-tray';
    tray.setAttribute('aria-label', 'Valdar vørur til samanberingar');
    document.body.append(tray);
    const announcement = document.createElement('p');
    announcement.className = 'visually-hidden';
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    document.body.append(announcement);
    const originalPadding = document.body.style.paddingBottom;

    function announce(message) {
      announcement.textContent = message;
      if (page) page.querySelector('[data-ssp-status]').textContent = message;
    }

    function renderTray() {
      const items = selections();
      tray.hidden = !!page || items.length === 0;
      document.body.style.paddingBottom = !tray.hidden ? '100px' : originalPadding;
      tray.innerHTML = '<span>' + items.length + ' av 2 vørum valdar</span>'
        + (items.length === 2 ? '<a href="' + escape(comparisonURL().href) + '">Samanber vørurnar</a>' : '<span>Vel eina vøru afturat</span>')
        + '<button type="button" data-ssp-clear>Rudda valið</button>'
        + (trayMessage ? '<p class="ssp-tray-message" role="status">' + escape(trayMessage) + '</p>' : '');
    }

    // Use inline feedback instead of browser alerts when the pair is full.
    CompareUtils.addToList = function (id, url) {
      const items = selections();
      if (items.some(item => String(item.id) === String(id))) return true;
      if (items.length >= MAX_PRODUCTS) {
        trayMessage = 'Tú kanst samanbera tvær vørur. Rudda valið fyri at velja aðrar.';
        announce(trayMessage);
        renderTray();
        return false;
      }
      const next = items.concat({ id: String(id), url });
      this.setSelectedProducts(next);
      this.updateCompareCheckboxes(next);
      FoxTheme.pubsub.publish('compare:add', { products: next, productId: String(id), productUrl: url });
      return true;
    };

    async function readProduct(item) {
      const url = productURL(item.url);
      url.searchParams.set('section_id', 'product-compare');
      if (item.variant) url.searchParams.set('variant', item.variant);
      url.searchParams.set('_sc', Date.now());
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Vøran kundi ikki heintast.');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const payload = doc.querySelector('[data-simme-compare-product]');
        if (!payload) throw new Error('Vøruupplýsingar mangla.');
        const product = JSON.parse(payload.textContent);
        if (item.id && String(product.id) !== String(item.id)) throw new Error('Vøran samsvarar ikki.');
        if (product.selected_variant && !product.variants.some(v => String(v.id) === String(product.selected_variant.id))) product.variants.push(product.selected_variant);
        if (!product.id || !Array.isArray(product.variants) || !product.variants.length) throw new Error('Vøran er ikki tøk.');
        if (item.variant && !product.variants.some(v => String(v.id) === String(item.variant))) throw new Error('Valda útgávan er ikki tøk.');
        product.chosen = item.variant || chosenVariant(product)?.id;
        product.specs ||= [];
        return product;
      } finally {
        clearTimeout(timer);
      }
    }

    function productName(product) {
      if (/stratocaster/i.test(product.title)) return 'Stratocaster';
      if (/telecaster/i.test(product.title)) return 'Telecaster';
      return product.title;
    }

    function specificationRows(products) {
      const names = {
        'instrument-material': 'Tilfar', 'guitar-size': 'Stødd',
        'instrument-string-material': 'Streingjatilfar', 'drum-configuration': 'Trummusett'
      };
      const normalize = label => String(label || '').replace(/\u00ad/g, '').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
      const keys = new Map();
      const structured = products.map(product => {
        const values = new Map();
        const add = (label, value) => {
          label = String(label || '').replace(/\u00ad/g, '').replace(/\s+/g, ' ').trim();
          value = String(value ?? '').replace(/\u00ad/g, '').replace(/\s+/g, ' ').trim();
          if (!label || !value || /^(?:https?|www|tel|email)$/i.test(label)) return;
          const key = normalize(label);
          if (!keys.has(key)) keys.set(key, label);
          const previous = values.get(key);
          if (!previous) values.set(key, value);
          else if (!previous.split(' · ').includes(value)) values.set(key, previous + ' · ' + value);
        };
        for (const spec of product.specs || []) {
          add(names[spec.key] || spec.label || spec.key, spec.value);
        }
        const doc = new DOMParser().parseFromString(product.description || '', 'text/html');
        for (const element of doc.querySelectorAll('tr,li,p')) {
          if (element.matches('p') && element.closest('li,td,th')) continue;
          if (element.matches('li') && element.querySelector('ul,ol,table')) continue;
          const cells = element.matches('tr') ? [...element.querySelectorAll('th,td')] : [];
          if (cells.length === 2) {
            add(cells[0].textContent, cells[1].textContent);
          } else if (!element.matches('tr')) {
            const match = element.textContent.trim().match(/^([^:\n]{2,100}):\s*([\s\S]+)$/);
            if (match) add(match[1], match[2]);
          }
        }
        return values;
      });
      if (!keys.size) {
        keys.set('vøruslag', 'Vøruslag');
        keys.set('merki', 'Merki');
        structured.forEach((values, i) => {
          if (products[i].type || products[i].category) values.set('vøruslag', products[i].type || products[i].category);
          if (products[i].vendor) values.set('merki', products[i].vendor);
        });
      }
      return [...keys].map(([key, label]) => ({
        label, values: structured.map(values => values.has(key) ? { value: values.get(key) } : null)
      }));
    }

    function productCard(product) {
      const variant = chosenVariant(product);
      const name = productName(product);
      const model = product.title === name ? (product.vendor || '') : product.title.replace(new RegExp(name + '®?', 'i'), '').replace(/\s+/g, ' ').trim();
      const picker = product.variants.length > 1 ? '<label for="ssp-variant-' + product.id + '">Vel útgávu</label><select id="ssp-variant-' + product.id + '" data-ssp-variant="' + product.id + '">'
        + product.variants.map(option => '<option value="' + option.id + '"' + (String(option.id) === String(variant?.id) ? ' selected' : '') + '>' + escape(option.title) + (option.available ? '' : ' — ikki tøk') + '</option>').join('') + '</select>' : '';
      const tax = page.dataset.taxIncluded === 'true' ? '<p class="ssp-tax">MVG íroknað</p>' : '';
      return '<article class="ssp-product" aria-labelledby="ssp-title-' + product.id + '">'
        + ((variant?.image || product.image) ? '<img class="ssp-photo" src="' + escape(variant?.image || product.image) + '" alt="' + escape(product.title) + '" width="320" height="320">' : '<div class="ssp-photo ssp-no-photo">Mynd manglar</div>')
        + '<h2 id="ssp-title-' + product.id + '">' + escape(name) + '</h2><p class="ssp-model">' + escape(model) + '</p>' + picker
        + '<p class="ssp-price">' + (variant ? escape(money(variant.price)) : 'Prísur ikki tøkur') + '</p>' + tax
        + '<p class="ssp-stock">' + escape(availableText(variant)) + '</p>'
        + purchaseAction(product)
        + '<button class="ssp-remove" type="button" data-ssp-remove="' + product.id + '" aria-label="Strika ' + escape(product.title) + ' úr samanberingini">Vel aðra vøru</button></article>';
    }

    function purchaseAction(product) {
      const variant = chosenVariant(product);
      if (!variant?.available) return '<button type="button" class="ssp-buy" disabled>Ikki tøk</button>';
      if (product.requires_selling_plan || product.purchase_options || product.variants_count > product.variants.length || variant.requires_components) {
        const url = productURL(product.url);
        url.searchParams.set('variant', variant.id);
        return '<a class="ssp-buy" href="' + escape(url.href) + '">Vel keypsmøguleikar</a>';
      }
      const quantity = Math.max(1, Number(variant.minimum) || 1);
      return '<form class="ssp-cart-form" action="' + escape(root + 'cart/add') + '" method="post">'
        + '<input type="hidden" name="id" value="' + variant.id + '">'
        + '<input type="hidden" name="quantity" value="' + quantity + '">'
        + '<input type="hidden" name="return_to" value="' + escape(root + 'cart') + '">'
        + '<button type="submit" class="ssp-buy" aria-label="Legg ' + escape(product.title + (variant.title === 'Default Title' ? '' : ' — ' + variant.title)) + ' í kurvina">Legg í kurvina</button>'
        + (quantity > 1 ? '<p class="ssp-tax">Minsta bílegging: ' + quantity + '</p>' : '') + '</form>';
    }

    function detailLink(product) {
      const url = productURL(product.url);
      const variant = chosenVariant(product);
      if (variant) url.searchParams.set('variant', variant.id);
      return '<a class="ssp-link" href="' + escape(url.href) + '">Sí ' + escape(productName(product)) + '</a>';
    }

    function renderPage() {
      const content = page.querySelector('[data-ssp-content]');
      const products = renderedProducts;
      page.querySelector('h1').textContent = products.length && products.every(product => /guitar/i.test(product.type + ' ' + product.title)) ? 'Samanber guitarar' : 'Samanber vørur';
      document.title = page.querySelector('h1').textContent + ' – Simme Musikkhús';
      const empty = '<section class="ssp-product"><h2>Vel eina vøru</h2><p>Vel tvær vørur, ið tú vilt samanbera.</p><a class="ssp-link" href="' + escape(root + 'collections/all') + '">Finn vøru</a></section>';
      let html = '<div class="ssp-pair">' + products.map(productCard).join('') + empty.repeat(MAX_PRODUCTS - products.length) + '</div>';
      if (products.length === 2) {
        const variants = products.map(chosenVariant);
        if (variants.every(Boolean)) {
          const difference = Math.abs(variants[0].price - variants[1].price);
          const cheaper = products[variants[0].price < variants[1].price ? 0 : 1];
          html += '<p class="ssp-price-note">' + (difference ? escape(productName(cheaper)) + ' kostar <strong>' + escape(money(difference)) + ' minni.</strong>' : 'Vørurnar hava sama prís.') + '</p>';
        }
        const rows = specificationRows(products);
        html += '<h3>Allar vøruupplýsingar</h3>';
        html += rows.length ? rows.map(row => '<div class="ssp-row"><p class="ssp-label">' + escape(row.label) + '</p><div class="ssp-pair">'
          + row.values.map(value => '<p class="ssp-value">' + escape(value?.value || 'Ikki upplýst') + (value?.note ? '<span class="ssp-explain">' + escape(value.note) + '</span>' : '') + '</p>').join('') + '</div></div>').join('')
          : '<p>Tøkniligar upplýsingar eru ikki skrásettar fyri hesar vørur. Sí vørusíðurnar fyri nærri kunning.</p>';
        html += '<div class="ssp-pair ssp-bottom">' + products.map(product => '<div>' + purchaseAction(product) + detailLink(product) + '</div>').join('') + '</div>'
          + '<button type="button" class="ssp-remove ssp-share" data-ssp-share>Avrita leinkju til samanberingina</button>';
      }
      content.innerHTML = html;
      content.removeAttribute('aria-busy');
    }

    function saveProducts() {
      const items = renderedProducts.map(product => ({ id: String(product.id), handle: product.handle, url: product.url, title: product.title, variant: chosenVariant(product)?.id }));
      CompareUtils.setSelectedProducts(items);
      CompareUtils.updateCompareCheckboxes(items);
      history.replaceState(null, '', comparisonURL(items));
    }

    async function loadPage(items) {
      const version = ++requestVersion;
      const status = page.querySelector('[data-ssp-status]');
      status.textContent = 'Heinti vørurnar…';
      const results = await Promise.allSettled(items.map(readProduct));
      if (version !== requestVersion) return;
      renderedProducts = results.filter(result => result.status === 'fulfilled').map(result => result.value);
      renderPage();
      const failed = results.some(result => result.status === 'rejected');
      status.innerHTML = failed ? 'Ein vøra kundi ikki heintast. <button type="button" class="ssp-remove" data-ssp-retry>Royn aftur</button>' : '';
      if (!failed) saveProducts();
    }

    document.addEventListener('click', event => {
      const button = event.target.closest('[data-ssp-clear],[data-ssp-remove],[data-ssp-share],[data-ssp-retry],.compare-bar__open');
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (button.matches('.compare-bar__open')) location.href = comparisonURL().href;
      if (button.matches('[data-ssp-clear]')) {
        CompareUtils.clearList(); trayMessage = ''; renderTray(); announce('Valið er ruddað.');
      }
      if (button.dataset.sspRemove) {
        renderedProducts = renderedProducts.filter(product => String(product.id) !== button.dataset.sspRemove);
        saveProducts(); renderPage();
        page.querySelector('.ssp-product:last-child a')?.focus();
      }
      if (button.matches('[data-ssp-retry]')) loadPage(requestedItems());
      if (button.matches('[data-ssp-share]')) {
        const link = comparisonURL(renderedProducts.map(product => ({ ...product, variant: chosenVariant(product)?.id }))).href;
        navigator.clipboard.writeText(link).then(() => announce('Leinkjan er avritað.')).catch(() => {
          page.querySelector('[data-ssp-status]').innerHTML = '<label>Avrita leinkjuna<input class="ssp-share-input" readonly value="' + escape(link) + '"></label>';
        });
      }
    }, true);

    page?.addEventListener('submit', async event => {
      const form = event.target.closest('.ssp-cart-form');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('button');
      if (button.disabled) return;
      button.disabled = true;
      button.textContent = 'Leggi í kurvina…';
      announce('Leggi vøruna í kurvina…');
      try {
        const response = await fetch(root + 'cart/add.js', { method:'POST', headers:{Accept:'application/json'}, body:new FormData(form) });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'Vøran kundi ikki leggjast í kurvina.');
        location.assign(root + 'cart');
      } catch (error) {
        announce(error.message || 'Samband fekst ikki. Royn aftur.');
        button.disabled = false; button.textContent = 'Legg í kurvina';
      }
    });

    page?.addEventListener('change', event => {
      const id = event.target.dataset.sspVariant;
      if (!id) return;
      renderedProducts.find(product => String(product.id) === id).chosen = event.target.value;
      saveProducts(); renderPage();
      page.querySelector('[data-ssp-variant="' + id + '"]')?.focus();
    });

    ['compare:add', 'compare:remove'].forEach(topic => FoxTheme.pubsub.subscribe(topic, () => {
      trayMessage = ''; renderTray(); announce(selections().length + ' av 2 vørum valdar.');
    }));
    window.addEventListener('storage', event => {
      if (event.key === CompareUtils.cookieName) { renderTray(); CompareUtils.updateCompareCheckboxes(); }
    });

    // Product-page entry point; the actual product page still owns purchase options and apps.
    const productInfo = document.querySelector('[id^="ProductInfo-"]');
    if (productInfo) {
      const add = document.createElement('button');
      add.type = 'button'; add.className = 'ssp-pdp'; add.textContent = 'Samanber hesa vøru';
      productInfo.querySelector('h1')?.insertAdjacentElement('afterend', add);
      add.addEventListener('click', async () => {
        add.disabled = true;
        try {
          const product = await readProduct({ url: location.pathname, variant: new URL(location.href).searchParams.get('variant') });
          if (CompareUtils.addToList(String(product.id), product.url)) {
            CompareUtils.setSelectedProducts(selections().map(item => String(item.id) === String(product.id)
              ? { ...item, handle: product.handle, title: product.title, variant: chosenVariant(product)?.id } : item));
            renderTray(); add.textContent = 'Vøran er vald til samanbering';
          }
        } catch { announce('Vøran kundi ikki veljast. Royn aftur.'); }
        finally { add.disabled = false; }
      });
    }

    function requestedItems() {
      const raw = new URL(location.href).searchParams.get('products');
      if (!raw) return selections();
      const seen = new Set();
      return raw.split(',').map(value => {
        const [handle, variant] = value.split(':');
        if (!handle || handle.length > 255 || /[\s/\\?#,:]/u.test(handle) || (variant && !/^\d+$/.test(variant)) || seen.has(handle)) return null;
        seen.add(handle);
        return { handle, url: root + 'products/' + handle, variant };
      }).filter(Boolean).slice(0, MAX_PRODUCTS);
    }

    window.addEventListener('pageshow', event => {
      if (event.persisted && page) loadPage(requestedItems());
    });

    renderTray();
    if (page) loadPage(requestedItems());
    else {
      // Old draft share links now open the normal comparison page, never the old popup.
      const legacy = new URL(location.href).searchParams.get('samanber');
      if (legacy) {
        const url = comparisonURL([]);
        url.searchParams.set('products', legacy.split(',').slice(0, MAX_PRODUCTS).join(','));
        location.replace(url.href);
      }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();

